import React, { useState, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  FunnelIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import Table from "../common/Table";
import EmailReportModal from "../modals/EmailReportModal";
import ActiveScreenReportFilterModal from "../filters/ActiveScreenReportFilterModal";
import { useActiveScreensListInfinite } from "../../hooks/useReports";
import useDebounce from "../../hooks/useDebounce";
import { ActiveScreenInfo, ReportFormat } from "../../schemas/reportSchema";
import { reportService } from "../../api/services/reportService";
import { toDisplayDateInput } from "../../utils/dateUtils";

const ActiveScreensReport: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => toDisplayDateInput();

  // Use selected date if available, otherwise use current date
  const dateToUse = selectedDate || getCurrentDate();

  // Rows load a page at a time as the user scrolls, so search runs on the
  // server (debounced) rather than over the pages already loaded.
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search);

  const {
    items: activeScreens,
    totalCount,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useActiveScreensListInfinite(dateToUse, debouncedSearch || null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [reportFormat, setReportFormat] = useState<ReportFormat>("EXCEL");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const handleFilterSubmit = (data: { date?: string }) => {
    setSelectedDate(data.date || null);
    setIsFilterModalOpen(false);
  };

  const handleFilterCancel = () => {
    setSelectedDate(null);
    setIsFilterModalOpen(false);
  };

  // Clear selected date but keep the modal open (used by Reset button inside modal)
  const handleFilterReset = () => {
    setSelectedDate(null);
  };

  const handleDownload = async (format: ReportFormat) => {
    if (totalCount === 0) {
      toast.error("No data available to download");
      return;
    }

    try {
      toast.info(`Generating ${format} report...`);

      const downloadResult =
        await reportService.downloadActiveScreenReport(dateToUse, format);

      // Convert base64 to blob
      const byteCharacters = atob(downloadResult.base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadResult.mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadResult.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    }
  };

  const handleSendEmail = async (email: string, format: ReportFormat) => {
    if (totalCount === 0) {
      toast.error("No report data available to send");
      return;
    }

    setIsSendingEmail(true);
    try {
      await reportService.sendActiveScreenReportEmail(email, dateToUse, format);
      toast.success("Report sent successfully via email");
      setIsEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Column definitions for Active Screens table. Sorting is disabled because
  // rows are server-ordered by screen name across all pages — a client-side
  // sort would only reorder the pages loaded so far.
  const activeScreensColumns: ColumnDef<ActiveScreenInfo>[] = useMemo(
    () => [
      {
        accessorKey: "screenName",
        header: "SCREEN NAME",
        enableSorting: false,
      },
      {
        accessorKey: "restaurantName",
        header: "RESTAURANT",
        enableSorting: false,
        cell: ({ row }) => row.original.restaurantName || "-",
      },
      {
        accessorKey: "city",
        header: "CITY",
        enableSorting: false,
        cell: ({ row }) => row.original.city || "-",
      },
      {
        accessorKey: "region",
        header: "REGION",
        enableSorting: false,
        cell: ({ row }) => row.original.region || "-",
      },
      {
        accessorKey: "orientation",
        header: "ORIENTATION",
        enableSorting: false,
        cell: ({ row }) => row.original.orientation || "-",
      },
    ],
    [],
  );

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading report: {error.message}</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Active Screens Report
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Showing active screens for:{" "}
            <span className="font-semibold text-primary-default">
              {formatDate(dateToUse)}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-white rounded-lg shadow px-6 py-3">
            <span className="text-sm text-gray-500">
              Total Active Screens:{" "}
            </span>
            <span className="text-lg font-bold text-primary-default">
              {totalCount}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
              title="Filter"
              onClick={() => setIsFilterModalOpen(true)}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
            <div className="relative">
              <button
                type="button"
                className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
                title="Download"
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg text-left"
                    onClick={() => {
                      setShowDownloadMenu(false);
                      setReportFormat("PDF");
                      handleDownload("PDF");
                    }}
                  >
                    Download PDF
                  </button>
                  <button
                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg text-left"
                    onClick={() => {
                      setShowDownloadMenu(false);
                      setReportFormat("EXCEL");
                      handleDownload("EXCEL");
                    }}
                  >
                    Download Excel
                  </button>
                </div>
              )}
            </div>
            <button
              className="p-2 bg-primary-default text-white rounded hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed"
              title="Mail"
              onClick={() => setIsEmailModalOpen(true)}
              disabled={totalCount === 0}
            >
              <EnvelopeIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Active Screens List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          Active Screens List
        </h2>
        <Table
          data={activeScreens}
          columns={activeScreensColumns}
          searchable={true}
          onSearch={setSearch}
          pagination={false}
          isLoading={isLoading}
          maxHeight="67vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
        />
      </div>

      {/* Filter Modal */}
      <ActiveScreenReportFilterModal
        visible={isFilterModalOpen}
        initialData={{ date: selectedDate || undefined }}
        onSubmit={handleFilterSubmit}
        onCancel={handleFilterCancel}
        onClose={() => setIsFilterModalOpen(false)}
        onReset={handleFilterReset}
      />

      {/* Email Modal */}
      {isEmailModalOpen && (
        <EmailReportModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSubmit={handleSendEmail}
          isLoading={isSendingEmail}
          reportName="Active Screens Report"
          defaultFormat={reportFormat}
        />
      )}
    </div>
  );
};

export default ActiveScreensReport;
